import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BillingPanel from "@/components/BillingPanel";
import ServiceTypesSettings from "@/components/ServiceTypesSettings";
import { useActivation } from "@/contexts/ActivationContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlassIcon, GearIcon, ShieldCheckIcon, ClockIcon, CreditCardIcon, WrenchIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import ManageAccessDialog from "@/components/ManageAccessDialog";
import { COMMON_TIMEZONES, useTimezone, fmtDT } from "@/lib/tz";

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [roleMatrix, setRoleMatrix] = useState({}); // role -> {module: {action:bool}}
  const [search, setSearch] = useState("");
  const [manageId, setManageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activated } = useActivation();

  const { tz, setTz, refresh: refreshTz } = useTimezone();
  const [tzDraft, setTzDraft] = useState(tz);
  const [tzSaving, setTzSaving] = useState(false);
  const [reservedThr, setReservedThr] = useState(2000);
  const [reservedThrDraft, setReservedThrDraft] = useState(2000);
  const [reservedSaving, setReservedSaving] = useState(false);
  // Per-category threshold overrides: { filter: 500, tire: 3000, ... }
  const [catThresholds, setCatThresholds] = useState({});
  const [catNewName, setCatNewName] = useState("");
  const [catNewValue, setCatNewValue] = useState("");
  const [catSaving, setCatSaving] = useState(false);
  useEffect(() => { setTzDraft(tz); }, [tz]);
  useEffect(() => {
    api.get("/company/preferences").then(({ data }) => {
      if (data?.reserved_alert_threshold != null) {
        setReservedThr(Number(data.reserved_alert_threshold));
        setReservedThrDraft(Number(data.reserved_alert_threshold));
      }
      if (data?.category_thresholds) setCatThresholds(data.category_thresholds || {});
    }).catch(() => {});
  }, []);

  const saveTimezone = async () => {
    setTzSaving(true);
    try {
      await api.put("/company/preferences", { timezone: tzDraft });
      setTz(tzDraft);
      await refreshTz();
      toast.success(`Timezone set to ${tzDraft}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save timezone");
    } finally { setTzSaving(false); }
  };

  const saveReservedThreshold = async () => {
    const val = Number(reservedThrDraft);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid amount ≥ 0"); return; }
    setReservedSaving(true);
    try {
      await api.put("/company/preferences", { reserved_alert_threshold: val });
      setReservedThr(val);
      toast.success(`Reserved-value alert threshold set to $${val.toLocaleString()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save threshold");
    } finally { setReservedSaving(false); }
  };

  const persistCatThresholds = async (next) => {
    setCatSaving(true);
    try {
      const { data } = await api.put("/company/preferences", { category_thresholds: next });
      setCatThresholds(data.category_thresholds || {});
      toast.success("Category thresholds updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save category thresholds");
    } finally { setCatSaving(false); }
  };
  const addCatThreshold = () => {
    const name = (catNewName || "").trim().toLowerCase();
    const val = Number(catNewValue);
    if (!name) { toast.error("Enter a category name"); return; }
    if (isNaN(val) || val < 0) { toast.error("Enter a valid amount ≥ 0"); return; }
    const next = { ...catThresholds, [name]: val };
    persistCatThresholds(next).then(() => { setCatNewName(""); setCatNewValue(""); });
  };
  const removeCatThreshold = (name) => {
    const next = { ...catThresholds };
    delete next[name];
    persistCatThresholds(next);
  };

  const load = async () => {
    if (!activated) { setLoading(false); return; }  // skip data load while gated
    setLoading(true);
    try {
      const [meta, usersRes] = await Promise.all([
        api.get("/permissions/modules"),
        api.get("/users"),
      ]);
      setModules(meta.data.modules);
      setActions(meta.data.actions);
      setUsers(usersRes.data);

      // Build role defaults preview by inspecting role_defaults from any one user per role
      // (cheap: just call effective-permissions for one of each role)
      const roles = ["admin", "manager", "dispatcher", "mechanic"];
      const matrix = {};
      for (const r of roles) {
        const sample = usersRes.data.find((u) => u.role === r);
        if (sample) {
          try {
            const { data } = await api.get(`/users/${sample.id}/effective-permissions`);
            matrix[r] = data.role_defaults;
          } catch (e) {
            // ignore missing role
          }
        }
      }
      setRoleMatrix(matrix);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.role?.toLowerCase().includes(s);
  });

  const roleTone = (r) => (r === "admin" ? "info" : r === "manager" ? "warning" : r === "dispatcher" ? "info" : "neutral");

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Admin"
        title="Settings"
        subtitle="Manage user access, roles, and per-user permission overrides."
      />

      <div className="p-8">
        <Tabs defaultValue={activated ? "users" : "billing"}>
          <TabsList className="rounded-sm" style={{ display: activated ? undefined : "none" }}>
            <TabsTrigger value="users" data-testid="settings-tab-users">
              <GearIcon size={14} weight="bold" className="mr-2" /> Users & Access
            </TabsTrigger>
            <TabsTrigger value="roles" data-testid="settings-tab-roles">
              <ShieldCheckIcon size={14} weight="bold" className="mr-2" /> Role Defaults
            </TabsTrigger>
            <TabsTrigger value="preferences" data-testid="settings-tab-preferences">
              <ClockIcon size={14} weight="bold" className="mr-2" /> Preferences
            </TabsTrigger>
            <TabsTrigger value="service-types" data-testid="settings-tab-service-types">
              <WrenchIcon size={14} weight="bold" className="mr-2" /> Service Types
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="settings-tab-billing">
              <CreditCardIcon size={14} weight="bold" className="mr-2" /> Billing
            </TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users" className="pt-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search users by name, email, or role"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 rounded-sm"
                  data-testid="settings-user-search"
                />
              </div>
              <div className="text-xs text-slate-500 font-mono">
                {filtered.length} of {users.length} users
              </div>
            </div>

            <div className="bg-white border border-slate-300 rounded-sm">
              <table className="fleet-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Overrides</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const overrideCount = u.permissions ? Object.keys(u.permissions).length : 0;
                    return (
                      <tr key={u.id} data-testid={`settings-user-row-${u.email}`}>
                        <td className="font-semibold">{u.name}</td>
                        <td className="font-mono text-xs">{u.email}</td>
                        <td><Badge tone={roleTone(u.role)}>{u.role}</Badge></td>
                        <td>
                          {u.is_active === false ? (
                            <Badge tone="danger">Inactive</Badge>
                          ) : (
                            <Badge tone="success">Active</Badge>
                          )}
                        </td>
                        <td className="font-mono text-xs">
                          {overrideCount > 0 ? (
                            <span className="text-amber-700 font-semibold">{overrideCount} module(s)</span>
                          ) : (
                            <span className="text-slate-400">None (uses role defaults)</span>
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setManageId(u.id)}
                            className="rounded-sm"
                            data-testid={`settings-manage-${u.email}`}
                          >
                            <GearIcon size={14} weight="bold" className="mr-1.5" />
                            Manage Access
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-slate-500 py-8">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ROLES TAB */}
          <TabsContent value="roles" className="pt-6 space-y-6">
            <div className="text-xs text-slate-600 max-w-3xl">
              These are the default permissions granted by each role. Users inherit these and you can override any cell per user from the Users tab.
            </div>
            {Object.entries(roleMatrix).map(([role, mat]) => (
              <div key={role} className="bg-white border border-slate-300 rounded-sm" data-testid={`role-matrix-${role}`}>
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="font-display text-lg font-black tracking-tight capitalize">{role}</div>
                  <Badge tone={roleTone(role)}>Default Permissions</Badge>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-mono text-[10px] uppercase tracking-wider text-slate-600">Module</th>
                        {actions.map((a) => (
                          <th key={a} className="p-2 font-mono text-[10px] uppercase tracking-wider text-slate-600 text-center">{a}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((m) => (
                        <tr key={m.key} className="border-t border-slate-100">
                          <td className="p-2 font-semibold text-slate-800">{m.label}</td>
                          {actions.map((a) => (
                            <td key={a} className="p-2 text-center">
                              {mat?.[m.key]?.[a] ? (
                                <span className="inline-block w-2.5 h-2.5 bg-green-600 rounded-full" />
                              ) : (
                                <span className="inline-block w-2.5 h-2.5 bg-slate-200 rounded-full" />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {Object.keys(roleMatrix).length === 0 && !loading && (
              <div className="text-sm text-slate-500">No role samples available. Add at least one user per role to preview defaults.</div>
            )}
          </TabsContent>

          {/* PREFERENCES TAB */}
          <TabsContent value="preferences" className="pt-6 space-y-6">
            <div className="bg-white border border-slate-300 rounded-sm p-6 max-w-2xl" data-testid="tz-panel">
              <div className="font-display text-lg font-black tracking-tight mb-1">Timezone</div>
              <div className="text-xs text-slate-600 mb-4">
                All timestamps across the app — trip starts, shift punches, geofence events, messages — will be displayed in this timezone.
                Every user in your company sees times in the zone you pick here.
              </div>
              <div className="grid gap-3">
                <div>
                  <Label>Company timezone</Label>
                  <Select value={tzDraft} onValueChange={setTzDraft}>
                    <SelectTrigger data-testid="tz-select" className="rounded-sm max-w-md"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {COMMON_TIMEZONES.map((z) => (
                        <SelectItem key={z} value={z}>{z}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  let browserTz = "";
                  try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* ignore */ }
                  if (!browserTz || browserTz === tzDraft) return null;
                  return (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 flex items-center justify-between gap-3">
                      <span>Your browser timezone is <b className="font-mono">{browserTz}</b>. Use it?</span>
                      <Button size="sm" variant="outline" onClick={() => setTzDraft(browserTz)} data-testid="tz-detect-btn">Use browser timezone</Button>
                    </div>
                  );
                })()}
                <div className="text-xs font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                  Preview → <b>{fmtDT(new Date().toISOString(), tzDraft)}</b>
                </div>
                <div>
                  <Button onClick={saveTimezone} disabled={tzSaving || tzDraft === tz} data-testid="tz-save-btn">
                    {tzSaving ? "Saving…" : "Save timezone"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-300 rounded-sm p-6 max-w-2xl" data-testid="reserved-thr-panel">
              <div className="font-display text-lg font-black tracking-tight mb-1">Reserved parts — value alert</div>
              <div className="text-xs text-slate-600 mb-4">
                When the total dollar value of parts reserved for a specific truck or trailer crosses this threshold,
                a warning banner appears on the <b>Inventory → Reserved for units</b> tab so you can act on stale reservations.
              </div>
              <div className="grid gap-3">
                <div>
                  <Label>Threshold ($)</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      value={reservedThrDraft}
                      onChange={(e) => setReservedThrDraft(e.target.value)}
                      className="rounded-sm font-mono max-w-[180px]"
                      data-testid="reserved-thr-input"
                    />
                    <span className="text-xs text-slate-500 font-mono">Set to 0 to disable the alert entirely.</span>
                  </div>
                </div>
                <div>
                  <Button
                    onClick={saveReservedThreshold}
                    disabled={reservedSaving || Number(reservedThrDraft) === Number(reservedThr)}
                    data-testid="reserved-thr-save-btn"
                  >
                    {reservedSaving ? "Saving…" : "Save threshold"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-300 rounded-sm p-6 max-w-2xl" data-testid="cat-thresholds-panel">
              <div className="font-display text-lg font-black tracking-tight mb-1">Category threshold overrides</div>
              <div className="text-xs text-slate-600 mb-4">
                Set a custom alert threshold for specific part categories (e.g., <b>$500</b> for filters, <b>$3,000</b> for tires).
                When a unit&apos;s reserved value in that category exceeds its override, the banner flags it. Categories without an override fall back to the default threshold above.
              </div>

              {Object.keys(catThresholds).length === 0 ? (
                <div className="text-xs font-mono text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-sm px-3 py-3 text-center mb-4">
                  No category overrides. Every category uses the default ${Number(reservedThr).toLocaleString()}.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-sm mb-4 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-mono text-[10px] uppercase text-slate-500">Category</th>
                        <th className="px-3 py-2 font-mono text-[10px] uppercase text-slate-500 text-right">Threshold</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(catThresholds).sort(([a], [b]) => a.localeCompare(b)).map(([cat, val]) => (
                        <tr key={cat} className="border-t border-slate-200" data-testid={`cat-thr-row-${cat}`}>
                          <td className="px-3 py-2 font-semibold capitalize">{cat}</td>
                          <td className="px-3 py-2 font-mono tabular-nums text-right">${Number(val).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeCatThreshold(cat)}
                              disabled={catSaving}
                              className="text-red-600 hover:underline text-xs font-semibold disabled:opacity-50"
                              data-testid={`cat-thr-remove-${cat}`}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2 items-end">
                <div>
                  <Label>Category name</Label>
                  <Input
                    placeholder="filter, tire, brake, fluid..."
                    value={catNewName}
                    onChange={(e) => setCatNewName(e.target.value)}
                    className="rounded-sm mt-1.5"
                    data-testid="cat-thr-name-input"
                  />
                </div>
                <div>
                  <Label>Threshold ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    placeholder="500"
                    value={catNewValue}
                    onChange={(e) => setCatNewValue(e.target.value)}
                    className="rounded-sm mt-1.5 font-mono"
                    data-testid="cat-thr-value-input"
                  />
                </div>
                <Button
                  onClick={addCatThreshold}
                  disabled={catSaving || !catNewName.trim() || catNewValue === ""}
                  data-testid="cat-thr-add-btn"
                >
                  {catSaving ? "Saving…" : "Add"}
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 font-mono mt-2">
                Common categories in your data: filter, tire, brake, fluid, engine, other
              </div>
            </div>
          </TabsContent>

          <TabsContent value="service-types" className="pt-6">
            <ServiceTypesSettings />
          </TabsContent>

          <TabsContent value="billing" className="pt-6">
            <BillingPanel />
          </TabsContent>
        </Tabs>
      </div>

      <ManageAccessDialog
        open={!!manageId}
        onOpenChange={(v) => { if (!v) setManageId(null); }}
        userId={manageId}
        onSaved={load}
      />
    </div>
  );
}
